import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, apiError, plainToHtml, htmlToPlain } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { RichTextEditor } from "../components/admin/RichTextEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const EMPTY = { question: "", answer: "", answer_html: "", order: 99, published: false };

export default function Faq() {
    const [items, setItems] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = () => api.get("/admin/faq").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...form, answer: htmlToPlain(form.answer_html) || form.answer };
            if (payload.answer.length < 5) return toast.error("La réponse doit contenir au moins 5 caractères.");
            if (editing.faq_id) await api.patch(`/admin/faq/${editing.faq_id}`, payload);
            else await api.post("/admin/faq", payload);
            toast.success("FAQ enregistrée.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div data-testid="admin-faq">
            <PageHeader title="Foire aux questions" subtitle="Les questions publiées apparaissent immédiatement sur le site.">
                <Button onClick={() => { setForm(EMPTY); setEditing({}); }} className="bg-forest text-cream gap-2" data-testid="faq-add-button"><Plus size={16} /> Ajouter</Button>
            </PageHeader>
            <div className="rounded-2xl bg-white border border-gold/25 shadow-sm divide-y">
                {(items || []).map((f) => (
                    <div key={f.faq_id} className="flex items-center gap-4 p-4" data-testid={`faq-row-${f.faq_id}`}>
                        <span className="font-mono text-xs text-gold w-8">{String(f.order).padStart(2, "0")}</span>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-forest text-sm truncate">{f.question}</p>
                            <p className="text-xs text-pineneedle truncate">{f.answer}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${f.published ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>{f.published ? "Publiée" : "Masquée"}</span>
                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { setForm({ question: f.question, answer: f.answer, answer_html: f.answer_html || plainToHtml(f.answer), order: f.order, published: f.published }); setEditing(f); }} data-testid={`faq-edit-${f.faq_id}`}><Pencil size={13} /></Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" onClick={() => setDeleteTarget(f)} data-testid={`faq-delete-${f.faq_id}`}><Trash2 size={13} /></Button>
                    </div>
                ))}
                {items && items.length === 0 && <p className="p-8 text-center text-pineneedle text-sm">Aucune question.</p>}
            </div>

            <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
                <DialogContent className="bg-cream" data-testid="faq-editor">
                    <DialogHeader><DialogTitle className="font-heading text-forest">{editing?.faq_id ? "Modifier la question" : "Nouvelle question"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Question</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question-input" /></div>
                        <div><Label>Réponse (mise en forme premium)</Label><RichTextEditor value={form.answer_html} onChange={(v) => setForm({ ...form, answer_html: v })} testid="faq-answer-editor" /></div>
                        <div><Label>Ordre d'affichage</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 99 })} data-testid="faq-order-input" /></div>
                        <label className="flex items-center gap-3 text-sm text-pineneedle"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="faq-published-switch" /> Publiée sur le site</label>
                        <Button onClick={save} className="w-full bg-forest text-cream" data-testid="faq-save">Enregistrer</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-cream">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette question ?</AlertDialogTitle>
                        <AlertDialogDescription>« {deleteTarget?.question} »</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-white" onClick={async () => { try { await api.delete(`/admin/faq/${deleteTarget.faq_id}`); toast.success("Question supprimée."); load(); } catch (e) { toast.error(apiError(e)); } }} data-testid="faq-delete-confirm">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
